import { GameRow, PlusIcon, SectionLabel, WordsHappenButton, WordsHappenFormAlert } from '@words-happen/ui-components';
import type React from 'react';
import { LobbyFrame } from './lobby-frame';
import styles from './lobby-screen.module.scss';
import { opponentColor } from './opponent-color';

/** A game being played. */
export interface GamesScreenActiveGame {
  id: string;
  opponentName: string;
  isMyTurn: boolean;
  myScore: number;
  opponentScore: number;
  /** Opens the game. Without it the row isn't a button. */
  onSelect?: () => void;
}

/** A game waiting for an opponent: by invitation, or by quick match. */
export interface GamesScreenWaitingGame {
  id: string;
  kind: 'invite' | 'quick-match';
  /** Shows the invitation again; invitations only. */
  onShareInvite?: () => void;
  onCancel: () => void;
  /** While the cancel is being sent. */
  cancelling?: boolean;
}

export interface GamesScreenFinishedGame {
  id: string;
  opponentName: string;
  result: 'won' | 'lost' | 'drew';
  myScore: number;
  opponentScore: number;
  /** Opens the game, to see the final board. Without it the row isn't a button. */
  onSelect?: () => void;
}

export interface GamesScreenProps {
  /** `loading` until the list first arrives; a refresh after that keeps `ready`. */
  status: 'loading' | 'ready' | 'load-error';
  onRetryLoad: () => void;
  /** The caller's turn first. */
  active: GamesScreenActiveGame[];
  waiting: GamesScreenWaitingGame[];
  finished: GamesScreenFinishedGame[];
  /** Why the last action failed, e.g. a cancel the server refused. */
  notice?: string;
  onNewGame: () => void;
  onBack: () => void;
  className?: string;
}

const RESULT_LABEL: Record<GamesScreenFinishedGame['result'], string> = { won: 'Won', lost: 'Lost', drew: 'Drew' };

const scoreLine = (mine: number, theirs: number) => `${mine}–${theirs}`;

const ActiveSection: React.FC<{ label: string; games: GamesScreenActiveGame[] }> = ({ label, games }) =>
  games.length ? (
    <section className={styles.section}>
      <SectionLabel>{label}</SectionLabel>
      <ul className={styles.list}>
        {games.map((game) => (
          <GameRow
            key={game.id}
            opponent={{ name: game.opponentName, color: opponentColor(game.opponentName) }}
            status={game.isMyTurn ? 'your-turn' : 'their-turn'}
            lastMove={`You ${scoreLine(game.myScore, game.opponentScore)}`}
            onClick={game.onSelect}
          />
        ))}
      </ul>
    </section>
  ) : null;

const WaitingRow: React.FC<{ game: GamesScreenWaitingGame }> = ({ game }) => (
  <li className={styles.waiting}>
    <div className={styles.waitingText}>
      <span className={styles.waitingTitle}>{game.kind === 'invite' ? 'Invitation sent' : 'Finding an opponent'}</span>
      <span className={styles.waitingMeta}>
        {game.kind === 'invite' ? 'Waiting for your friend to join' : 'Quick match: the next player to ask joins'}
      </span>
    </div>
    <div className={styles.inlineActions}>
      {game.onShareInvite ? (
        <WordsHappenButton size="sm" onClick={game.onShareInvite} disabled={game.cancelling}>
          Share invite
        </WordsHappenButton>
      ) : null}
      <WordsHappenButton size="sm" variant="paper" onClick={game.onCancel} loading={game.cancelling}>
        Cancel
      </WordsHappenButton>
    </div>
  </li>
);

const NewGameButton: React.FC<{ onNewGame: () => void }> = ({ onNewGame }) => (
  <WordsHappenButton variant="primary" icon={<PlusIcon />} onClick={onNewGame}>
    New game
  </WordsHappenButton>
);

const GameLists: React.FC<Pick<GamesScreenProps, 'active' | 'waiting' | 'finished' | 'onNewGame'>> = ({
  active,
  waiting,
  finished,
  onNewGame,
}) => {
  if (!active.length && !waiting.length && !finished.length) {
    return (
      <div className={styles.card}>
        <p className={styles.lead}>No games yet. Start one and invite a friend, or get matched with another player.</p>
        <WordsHappenButton variant="primary" onClick={onNewGame}>
          Start a game
        </WordsHappenButton>
      </div>
    );
  }

  return (
    <>
      <ActiveSection label="Your turn" games={active.filter(({ isMyTurn }) => isMyTurn)} />
      <ActiveSection label="Their turn" games={active.filter(({ isMyTurn }) => !isMyTurn)} />
      {waiting.length ? (
        <section className={styles.section}>
          <SectionLabel>Waiting for an opponent</SectionLabel>
          <ul className={styles.list}>
            {waiting.map((game) => (
              <WaitingRow key={game.id} game={game} />
            ))}
          </ul>
        </section>
      ) : null}
      <NewGameButton onNewGame={onNewGame} />
      {finished.length ? (
        <section className={styles.section}>
          <SectionLabel>Finished</SectionLabel>
          <ul className={styles.list}>
            {finished.map((game) => (
              <GameRow
                key={game.id}
                opponent={{ name: game.opponentName, color: opponentColor(game.opponentName) }}
                status="ended"
                lastMove={`${RESULT_LABEL[game.result]} ${scoreLine(game.myScore, game.opponentScore)}`}
                onClick={game.onSelect}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
};

/**
 * The games list (/games): games being played, the caller's turn first; games waiting for an opponent, with Share
 * invite and Cancel; New game; and finished games. A + beside the title, which stays in view, starts a game too. It holds no state: the app loads the list, and keeps it current.
 */
export const GamesScreen: React.FC<GamesScreenProps> = ({
  status,
  onRetryLoad,
  active,
  waiting,
  finished,
  notice,
  onNewGame,
  onBack,
  className,
}) => (
  <LobbyFrame
    title="Your games"
    backLabel="Back to home"
    onBack={onBack}
    action={
      <WordsHappenButton
        size="sm"
        variant="primary"
        className={styles.titleAction}
        aria-label="Start a new game"
        icon={<PlusIcon size={22} />}
        onClick={onNewGame}
      />
    }
    className={className}
  >
    {notice ? <WordsHappenFormAlert>{notice}</WordsHappenFormAlert> : null}
    {status === 'loading' ? (
      <p className={styles.status} role="status">
        Loading your games…
      </p>
    ) : null}
    {status === 'load-error' ? (
      <div className={styles.error}>
        <WordsHappenFormAlert title="Couldn't load your games">
          Check your connection and try again.
        </WordsHappenFormAlert>
        <WordsHappenButton size="sm" onClick={onRetryLoad}>
          Try again
        </WordsHappenButton>
      </div>
    ) : null}
    {status === 'ready' ? (
      <GameLists active={active} waiting={waiting} finished={finished} onNewGame={onNewGame} />
    ) : null}
  </LobbyFrame>
);
